import { useId, useLayoutEffect, useState } from 'react';

import styles from './chartTradeMarkers.module.css';
import {
  TRADE_MARKER_BODY_RADIUS,
  TRADE_MARKER_BODY_SIZE,
  TRADE_MARKER_POINTER_HEIGHT,
  type TradeMarkerGroupGeometry,
} from './TimeSeriesChart/utils/geometry';

const MARKER_HALF = TRADE_MARKER_BODY_SIZE / 2;
const MARKER_EDGE = MARKER_HALF - TRADE_MARKER_BODY_RADIUS;
const MARKER_TIP_Y = MARKER_HALF + TRADE_MARKER_POINTER_HEIGHT;
const MARKER_ARC = `${TRADE_MARKER_BODY_RADIUS} ${TRADE_MARKER_BODY_RADIUS} 0 0 1`;

// Rounded square in local coordinates (body center at 0,0) with a downward
// triangle pointer spliced into the bottom edge, tip at (0, MARKER_TIP_Y).
const TRADE_MARKER_PATH = [
  `M ${-MARKER_EDGE} ${-MARKER_HALF}`,
  `H ${MARKER_EDGE}`,
  `A ${MARKER_ARC} ${MARKER_HALF} ${-MARKER_EDGE}`,
  `V ${MARKER_EDGE}`,
  `A ${MARKER_ARC} ${MARKER_EDGE} ${MARKER_HALF}`,
  `L 0 ${MARKER_TIP_Y}`,
  `L ${-MARKER_EDGE} ${MARKER_HALF}`,
  `A ${MARKER_ARC} ${-MARKER_HALF} ${MARKER_EDGE}`,
  `V ${-MARKER_EDGE}`,
  `A ${MARKER_ARC} ${-MARKER_EDGE} ${-MARKER_HALF}`,
  'Z',
].join(' ');

type ChartMarkerColors = {
  buy: string;
  sell: string;
};

type ChartTradeMarkersProps = {
  geometry: TradeMarkerGroupGeometry[];
  colors: ChartMarkerColors;
  hoveredMarkerKey?: string;
};

type ChartTradeMarkerShapeProps = {
  group: TradeMarkerGroupGeometry;
  colors: ChartMarkerColors;
  hovered: boolean;
  shadowId: string;
  entranceDelay?: number;
};

const MARKER_STAGGER_STEP_MS = 40;
const MARKER_STAGGER_TOTAL_MS = 200;
const NO_ENTRANCE_DELAYS = new Map<string, number>();

/**
 * Staggers the first batch of markers a chart renders. Markers arriving afterwards -
 * paginated history, refreshes - appear immediately. Remounting the chart starts a new batch.
 */
const useMarkerEntranceDelays = (geometry: TradeMarkerGroupGeometry[]) => {
  const [entranceDelays, setEntranceDelays] = useState<Map<string, number>>();

  useLayoutEffect(() => {
    if (!entranceDelays && geometry.length > 0) {
      const step =
        geometry.length > 1
          ? Math.min(MARKER_STAGGER_STEP_MS, MARKER_STAGGER_TOTAL_MS / (geometry.length - 1))
          : 0;

      setEntranceDelays(
        new Map(
          geometry
            .toSorted((a, b) => a.markers[0].timestamp - b.markers[0].timestamp)
            .map(({ key }, index): [string, number] => [key, Math.round(index * step)]),
        ),
      );
    }
  }, [entranceDelays, geometry]);

  return entranceDelays ?? NO_ENTRANCE_DELAYS;
};

// U+2212 rather than a hyphen so the sell glyph matches the width of `+`.
const markerGlyph = (group: TradeMarkerGroupGeometry) => {
  if (group.count > 1) return group.count > 9 ? '9+' : String(group.count);
  return group.direction === 'buy' ? '+' : '\u2212';
};

const ChartTradeMarkerShape = ({
  group,
  colors,
  hovered,
  shadowId,
  entranceDelay,
}: ChartTradeMarkerShapeProps) => {
  // Strip colons from React IDs because SVG-to-image tools may not
  // resolve unescaped colons in `url(#...)` references.
  const clipId = `chart-trade-marker-clip-${useId().replaceAll(':', '')}`;
  const stroke = 'var(--color-special-background-homepage)';
  const strokeWidth = hovered ? 2 : 1;
  const title =
    group.count > 1
      ? `${group.count} trades`
      : group.direction === 'buy'
        ? 'Buy trade'
        : 'Sell trade';

  return (
    <g
      data-testid={`chart-trade-marker-${group.key}`}
      transform={`translate(${group.x} ${group.y})`}
      filter={`url(#${shadowId})`}
    >
      <title>{title}</title>
      <g
        className={entranceDelay == null ? undefined : styles['marker-enter']}
        style={entranceDelay == null ? undefined : { animationDelay: `${entranceDelay}ms` }}
      >
        {group.direction === 'mixed' ? (
          <>
            <clipPath id={clipId}>
              <path d={TRADE_MARKER_PATH} />
            </clipPath>
            <rect
              x={-MARKER_HALF}
              y={-MARKER_HALF}
              width={MARKER_HALF}
              height={TRADE_MARKER_BODY_SIZE + TRADE_MARKER_POINTER_HEIGHT}
              fill={colors.buy}
              clipPath={`url(#${clipId})`}
            />
            <rect
              x={0}
              y={-MARKER_HALF}
              width={MARKER_HALF}
              height={TRADE_MARKER_BODY_SIZE + TRADE_MARKER_POINTER_HEIGHT}
              fill={colors.sell}
              clipPath={`url(#${clipId})`}
            />
            <path d={TRADE_MARKER_PATH} fill="none" stroke={stroke} strokeWidth={strokeWidth} />
          </>
        ) : (
          <path
            d={TRADE_MARKER_PATH}
            fill={colors[group.direction]}
            stroke={stroke}
            strokeWidth={strokeWidth}
          />
        )}
        <text
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={9}
          fontWeight={600}
          fill="var(--color-special-background-homepage)"
        >
          {markerGlyph(group)}
        </text>
      </g>
    </g>
  );
};

export const ChartTradeMarkers = ({
  geometry,
  colors,
  hoveredMarkerKey,
}: ChartTradeMarkersProps) => {
  const shadowId = `chart-trade-marker-shadow-${useId().replaceAll(':', '')}`;
  const entranceDelays = useMarkerEntranceDelays(geometry);

  return (
    <g pointerEvents="none" aria-hidden="true">
      <filter id={shadowId}>
        {/* flood-color goes through `style` because a var() in the presentation
            attribute is not resolved everywhere. The token is dark in light mode
            and light in dark mode, so the halo always contrasts with the candles. */}
        <feDropShadow
          dx="0"
          dy="1"
          stdDeviation="1"
          floodOpacity="0.3"
          style={{ floodColor: 'var(--color-token-gray-1000)' }}
        />
      </filter>
      {geometry.map((group) => (
        <ChartTradeMarkerShape
          key={group.key}
          group={group}
          colors={colors}
          hovered={group.key === hoveredMarkerKey}
          shadowId={shadowId}
          entranceDelay={entranceDelays.get(group.key)}
        />
      ))}
    </g>
  );
};
